import React, { useEffect, useState } from 'react';

import { TIMEZONES, formatNumber, zoneAbbreviation } from '../ListingSources/format';
import { Menu } from './Menu';

// A property is checked with up to three LightBox requests: its address, its
// map pin and the parcel's contacts.
const REQUESTS_PER_PROPERTY = 3;

// What one AI answer costs on average, in US dollars, measured on the backtests.
const AI_COST_PER_PROPERTY = 0.005;

const CHECK_FIRST = [
    ['active', 'Active properties, then the rest'],
    ['newest', 'Newest properties first'],
];

const NO_STATUS_LABEL = 'No status';

function sameStatus(a, b) {
    return (a || null) === (b || null);
}

function draftOf(settings) {
    return {
        enabled: Boolean(settings.enabled),
        run_at: settings.run_at || '',
        timezone: settings.timezone,
        daily_requests: String(settings.daily_requests),
        excluded_statuses: (settings.excluded_statuses || []).slice(),
        check_first: settings.check_first,
        ai_enabled: Boolean(settings.ai_enabled),
        ai_monthly_cap_usd: String(settings.ai_monthly_cap_usd),
        ai_guidance: settings.ai_guidance || '',
    };
}

/**
 * What the owner research run is set to do, in the listing screen's dialog:
 * when it runs, how much LightBox it may use, which properties, which first.
 */
export default function SettingsModal({ settings, statuses, onSave, onClose }) {
    const [draft, setDraft] = useState(() => draftOf(settings));
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [failure, setFailure] = useState(null);

    useEffect(() => {
        const onKeyDown = (event) => event.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const original = draftOf(settings);
    const dirty = JSON.stringify(draft) !== JSON.stringify(original);

    const set = (field, value) => {
        setDraft({ ...draft, [field]: value });
        setSaved(false);
    };

    const isIncluded = (status) => !draft.excluded_statuses.some((excluded) => sameStatus(excluded, status));

    const toggle = (status) => set(
        'excluded_statuses',
        isIncluded(status)
            ? draft.excluded_statuses.concat([status || null])
            : draft.excluded_statuses.filter((excluded) => !sameStatus(excluded, status)),
    );

    const save = () => {
        setSaving(true);
        setFailure(null);
        onSave({
            enabled: draft.enabled,
            run_at: draft.run_at.trim(),
            timezone: draft.timezone,
            daily_requests: Number(draft.daily_requests),
            excluded_statuses: draft.excluded_statuses,
            check_first: draft.check_first,
            ai_enabled: draft.ai_enabled,
            ai_monthly_cap_usd: Number(draft.ai_monthly_cap_usd),
            ai_guidance: draft.ai_guidance.trim(),
        }).then(
            () => {
                setSaving(false);
                setSaved(true);
            },
            (error) => {
                setSaving(false);
                setFailure(error);
            }
        );
    };

    const invalid = (field) => failure && failure.errors && failure.errors[field];
    const limit = Number(draft.daily_requests);
    const cap = Number(draft.ai_monthly_cap_usd);
    const chosen = statuses.filter((row) => isIncluded(row.status)).reduce((total, row) => total + row.count, 0);

    return (
        <div className="ls-modal">
            <div className="ls-modal__scrim" onClick={onClose} />

            <div className="ls-modal__dialog" role="dialog" aria-modal="true" aria-label="Owner Research settings">
                <div className="ls-modal__head">
                    <span className="ls-modal__title">Owner Research settings</span>
                    <button type="button" className="ls-modal__close" aria-label="Close" onClick={onClose}>×</button>
                </div>

                <div className="ls-modal__body">
                    {failure && (
                        <div className="ls-modal__alert">
                            <span className="ls-alert__mark">!</span>
                            {failure.message}
                        </div>
                    )}

                    <div className="ls-switchrow">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={draft.enabled}
                            aria-label="Nightly run"
                            className={'ls-switch' + (draft.enabled ? ' is-on' : '')}
                            onClick={() => set('enabled', !draft.enabled)}
                        >
                            <span className="ls-switch__knob" />
                        </button>
                        <div>
                            <div className="ls-switchrow__title">Nightly run</div>
                            <div className="ls-switchrow__help">{draft.enabled ? 'Runs unattended' : 'Manual runs only'}</div>
                        </div>
                    </div>

                    <div className="ls-modal__columns">
                        <div>
                            <div className="ls-group">
                                <div className="ls-group__head">
                                    <span className="ls-group__title">Schedule</span>
                                </div>
                                <div className="ls-schedule">
                                    <label className="ls-field">
                                        <span className="ls-field__label">Start at</span>
                                        <input
                                            type="text"
                                            className={'ls-input ls-mono' + (invalid('run_at') ? ' is-invalid' : '')}
                                            value={draft.run_at}
                                            onChange={(event) => set('run_at', event.target.value)}
                                        />
                                    </label>
                                    <Menu
                                        label="Timezone"
                                        value={draft.timezone}
                                        options={TIMEZONES.map(([zone, name]) => [zone, name + ' — ' + zone])}
                                        onPick={(zone) => set('timezone', zone)}
                                    />
                                </div>
                                {/* Without it "02:00" reads as the viewer's own clock. */}
                                <div className="ls-note">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                                        <circle cx="6" cy="6" r="4.4" />
                                        <path d="M6 3.6 V6.2 L7.8 7.4" />
                                    </svg>
                                    {draft.run_at} {zoneAbbreviation(draft.timezone)} · {draft.timezone}
                                </div>
                            </div>

                            <div className="ls-group">
                                <div className="ls-group__head">
                                    <span className="ls-group__title">LightBox</span>
                                </div>
                                <label className="ls-field ls-field--spaced">
                                    <span className="ls-field__label">Requests per day</span>
                                    <div className="ls-inline">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className={'ls-input ls-input--narrow ls-mono' + (invalid('daily_requests') ? ' is-invalid' : '')}
                                            value={draft.daily_requests}
                                            onChange={(event) => set('daily_requests', event.target.value)}
                                        />
                                        <span className="ls-inline__hint">
                                            {limit > 0
                                                ? '≈ ' + formatNumber(Math.floor(limit / REQUESTS_PER_PROPERTY)) + ' properties a night'
                                                : '0 = no limit'}
                                        </span>
                                    </div>
                                </label>
                                <div className="ls-note">
                                    Up to {REQUESTS_PER_PROPERTY} requests a property
                                    {limit >= REQUESTS_PER_PROPERTY && ' · about ' + formatNumber(Math.ceil(chosen / Math.floor(limit / REQUESTS_PER_PROPERTY))) + ' nights for all'}
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="ls-group">
                                <div className="ls-group__head">
                                    <span className="ls-group__title">Which properties</span>
                                    <span className="ls-group__meta">{formatNumber(chosen)} properties</span>
                                </div>
                                <div className="ls-chips">
                                    {statuses.map((row) => (
                                        <button
                                            key={row.status || NO_STATUS_LABEL}
                                            type="button"
                                            className={'ls-toggle' + (isIncluded(row.status) ? ' is-on' : '')}
                                            onClick={() => toggle(row.status)}
                                        >
                                            {row.status || NO_STATUS_LABEL}
                                            <span className="ls-filter__count" style={{ marginLeft: 8 }}>{formatNumber(row.count)}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className="ls-note">No owner or Need Owner · not Researched</div>
                            </div>

                            <div className="ls-group">
                                <div className="ls-group__head">
                                    <span className="ls-group__title">Order</span>
                                </div>
                                <div className="ls-field--spaced">
                                    <Menu
                                        label="Check first"
                                        value={draft.check_first}
                                        options={CHECK_FIRST}
                                        onPick={(value) => set('check_first', value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="or-ai-section">
                        <div className="ls-switchrow">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={draft.ai_enabled}
                                aria-label="AI decides what the rules cannot"
                                className={'ls-switch' + (draft.ai_enabled ? ' is-on' : '')}
                                onClick={() => set('ai_enabled', !draft.ai_enabled)}
                            >
                                <span className="ls-switch__knob" />
                            </button>
                            <div>
                                <div className="ls-switchrow__title">AI</div>
                                <div className="ls-switchrow__help">{draft.ai_enabled ? 'Decides what the rules cannot prove' : 'Rules only'}</div>
                            </div>
                        </div>
                        <div className="ls-modal__columns">
                            <label className="ls-field">
                                <span className="ls-field__label">Spend a month</span>
                                <div className="ls-inline">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        className={'ls-input ls-input--narrow ls-mono' + (invalid('ai_monthly_cap_usd') ? ' is-invalid' : '')}
                                        value={draft.ai_monthly_cap_usd}
                                        onChange={(event) => set('ai_monthly_cap_usd', event.target.value)}
                                    />
                                    <span className="ls-inline__hint">
                                        {cap > 0
                                            ? 'US dollars · ≈ ' + formatNumber(Math.floor(cap / AI_COST_PER_PROPERTY)) + ' properties'
                                            : '0 = no limit'}
                                    </span>
                                </div>
                            </label>
                            <label className="ls-field">
                                <span className="ls-field__label">Guidance</span>
                                <textarea
                                    className={'ls-input or-textarea' + (invalid('ai_guidance') ? ' is-invalid' : '')}
                                    rows={3}
                                    placeholder="Read with every question. It can add caution, never relax the rules."
                                    value={draft.ai_guidance}
                                    onChange={(event) => set('ai_guidance', event.target.value)}
                                />
                            </label>
                        </div>
                    </div>
                </div>

                <div className={'ls-modal__foot' + (dirty ? ' is-dirty' : '')}>
                    <span className="ls-modal__hint">{saved && !dirty ? 'Saved' : dirty ? 'Unsaved changes' : ''}</span>
                    <button type="button" className="ls-discard" onClick={() => setDraft(draftOf(settings))}>Discard</button>
                    <button type="button" className="ls-save" disabled={!dirty || saving} onClick={save}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}
